if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        this.updateThumbnailScrollState = debounce(this.updateThumbnailScrollState.bind(this), 40);
        if (this.thumbnailList) {
          this.onThumbnailListScroll = () => this.updateThumbnailScrollState();
          this.thumbnailList.addEventListener('scroll', this.onThumbnailListScroll, { passive: true });
        }
        this.updateThumbnailColumnHeight = debounce(this.updateThumbnailColumnHeight.bind(this), 80);
        this.onWindowResize = this.updateThumbnailColumnHeight;
        window.addEventListener('resize', this.onWindowResize);
        if (window.ResizeObserver) {
          this.viewerResizeObserver = new ResizeObserver(() => this.updateThumbnailColumnHeight());
          this.viewerResizeObserver.observe(this.elements.viewer);
        }
        this.updateThumbnailColumnHeight();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
        if (this.thumbnailList && this.onThumbnailListScroll) {
          this.thumbnailList.removeEventListener('scroll', this.onThumbnailListScroll);
        }
        if (this.viewerResizeObserver) this.viewerResizeObserver.disconnect();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateThumbnailColumnHeight();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (thumbnailList && window.getComputedStyle(thumbnailList).flexDirection === 'column') {
          const centeredTop = thumbnail.offsetTop - thumbnailList.clientHeight / 2 + thumbnail.clientHeight / 2;
          thumbnailList.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
          this.updateThumbnailScrollState();
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      updateThumbnailColumnHeight() {
        if (!this.elements.viewer || !this.elements.thumbnails) return;
        if (!this.mql.matches) {
          this.style.removeProperty('--product-gallery-height');
          return;
        }
        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active .product-media-container') ||
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer;
        const activeHeight = Math.round(activeMedia.getBoundingClientRect().height);
        if (activeHeight > 0) this.style.setProperty('--product-gallery-height', `${activeHeight}px`);
        this.updateThumbnailScrollState();
      }

      updateThumbnailScrollState() {
        if (!this.elements.thumbnails || !this.thumbnailList) return;

        const isDesktopColumn =
          this.mql.matches && window.getComputedStyle(this.thumbnailList).flexDirection === 'column';
        if (!isDesktopColumn) {
          this.elements.thumbnails.classList.remove('has-overflow', 'can-scroll-up', 'can-scroll-down');
          return;
        }

        const maxScroll = this.thumbnailList.scrollHeight - this.thumbnailList.clientHeight;
        const hasOverflow = maxScroll > 2;
        const canScrollUp = this.thumbnailList.scrollTop > 2;
        const canScrollDown = this.thumbnailList.scrollTop < maxScroll - 2;

        this.elements.thumbnails.classList.toggle('has-overflow', hasOverflow);
        this.elements.thumbnails.classList.toggle('can-scroll-up', hasOverflow && canScrollUp);
        this.elements.thumbnails.classList.toggle('can-scroll-down', hasOverflow && canScrollDown);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
